import {
  FacebookLeadgenBodyStyle,
  FacebookLeadgenButtonType,
  FacebookLeadgenFormType,
  FacebookLeadgenLocale,
  FacebookLeadgenQuestionType,
  FacebookLeadgenSharingOptions,
  LeadCard,
  LeadCardQuestion,
} from './LeadCard';
import { exampleAdImage } from './AdImage';

interface LeadCardCustomDisclaimerCheckBox {
  isRequired: boolean;
  isCheckedByDefault: boolean;
  text: string;
}

export interface LeadCardPrivacySettings {
  link: string;
  text?: string;
  customDisclaimer?: {
    title: string;
    body: string;
    checkboxes: LeadCardCustomDisclaimerCheckBox[];
  };
}

export interface LeadCardThankYou {
  headline: string;
  description: string;
  callToAction: FacebookLeadgenButtonType;
  buttonText: string;
  link: string;
  phoneNumber?: string;
  countryCode?: string;
}

export interface LeadCardIntro {
  imageID?: number;
  imageURL?: string;
  greeting?: {
    headline: string;
    bodyStyle: FacebookLeadgenBodyStyle;
    bodyItems: string[];
  };
}

export interface LeadCardCreateDTO {
  formName: string;
  formType: FacebookLeadgenFormType;
  intro: LeadCardIntro;
  questionsDesc?: string;
  questions: LeadCardQuestion[];
  privacy: LeadCardPrivacySettings;
  thankYouPage?: LeadCardThankYou;
  settings: {
    locale: FacebookLeadgenLocale;
    sharing: FacebookLeadgenSharingOptions;
    trackingParams?: {
      [key: string]: string;
    };
  };
}

export const exampleLeadCardCreateDTO: LeadCardCreateDTO = {
  formName: 'New Form',
  formType: 'more_volume',
  intro: {
    imageID: exampleAdImage.id,
    imageURL: exampleAdImage.url,
    greeting: {
      headline: 'Headline',
      bodyStyle: 'LIST_STYLE',
      bodyItems: ['Here is my text', 'again again'],
    },
  },
  questionsDesc: 'Please fill out the form',
  questions: [
    { type: FacebookLeadgenQuestionType.CITY },
    {
      type: FacebookLeadgenQuestionType.CUSTOM,
      format: 'multiple-choice',
      options: [
        { key: 'A', value: 'a' },
        { key: 'B', value: 'b' },
      ],
      key: 'a_or_b',
      label: 'Choose a or b',
    },
  ],
  privacy: {
    link: 'url.com',
    text: 'example.com',
    customDisclaimer: {
      title: 'please note',
      body: 'review please',
      checkboxes: [
        { isRequired: true, isCheckedByDefault: true, text: 'i agree' },
      ],
    },
  },
  thankYouPage: {
    headline: 'headline here',
    description: 'thank you so much',
    callToAction: FacebookLeadgenButtonType.VIEW_WEBSITE,
    buttonText: 'click me',
    link: 'link.com',
  },
  settings: {
    locale: FacebookLeadgenLocale.EN_US,
    sharing: 'restricted',
    trackingParams: { foo: 'bar' },
  },
};

// Overrides apply to every business in a share. "business_website" and "page_phone" are filled in
// per business; "source" keeps what the source form has.
export type ShareLinkMode = 'source' | 'business_website' | 'custom';

export interface ShareLinkOverride {
  mode: ShareLinkMode;
  url?: string; // only for 'custom'
}

export interface SharePrivacyLinkOverride {
  mode: 'source' | 'custom';
  url?: string; // only for 'custom'
}

export interface SharePhoneOverride {
  mode: 'source' | 'page_phone' | 'custom';
  number?: string; // E.164, only for 'custom'
}

export interface ShareLeadCardOverrides {
  privacyLink?: SharePrivacyLinkOverride;
  thankYouLink?: ShareLinkOverride;
  thankYouPhone?: SharePhoneOverride; // only used when the thank-you button calls the business
}

// Businesses one share request copies to. Each is a Meta create run in turn, and the load balancer
// cuts a request off at 120s, so a bigger share is sent in batches of this size.
export const SHARE_TARGET_LIMIT = 10;

export interface ShareLeadCardDTO {
  businessIDs: number[];
  overrides?: ShareLeadCardOverrides;
}

// One entry per target business. Meta can refuse one Page and accept another,
// so a share reports each target on its own rather than failing as a whole.
export type ShareLeadCardResult =
  | { businessID: number; status: 'shared'; leadCard: LeadCard }
  | { businessID: number; status: 'failed'; error: string };

const comparableWebsite = (url?: string | null) =>
  url
    ?.trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

// Whether a link on a lead card is just the business's own website, and so
// should follow the form to whichever business it is shared to.
export const isBusinessWebsite = (
  url?: string | null,
  website?: string | null,
): boolean => {
  const link = comparableWebsite(url);
  return !!link && link === comparableWebsite(website);
};
